import { type DriverSpeedingTableRow, PageType, type Severity, type SpeedingSummary, type VehicleSpeedingTableRow } from '@cobliteam/common-types';
import camelcaseKeys from 'camelcase-keys';
import moment from 'moment';
import snakecaseKeys from 'snakecase-keys';
import {
  type Configuration,
  type HeatmapChart,
  ReportAggregatedBy,
  type SpeedingChartRequest,
  type SpeedingReportRequest,
  type SpeedingRequest,
  type TimeseriesChart,
} from '../..';
import HttpMethod from '../../request/HttpMethod';
import Requester from '../../request/Requester';
import TimeZone from '../../request/Timezone';
import { toCapitalSeverity } from './APITransformers';

export class AnalyticsSpeedingRequester {
  private analyticsSpeedingBasePath = '/analytics/v1/speeding';

  private requester: Requester;

  constructor(configuration: Configuration) {
    this.requester = new Requester(configuration);
  }

  public getSpeedingSummary = async (body: SpeedingRequest, aggregatedBy: PageType): Promise<SpeedingSummary> => {
    const requestBody = this.getSpeedingRequestBody(body, aggregatedBy);
    const aggregationRouteField = aggregatedBy === PageType.VEHICLE_GROUP ? PageType.VEHICLE : aggregatedBy;
    const requestData = {
      endpoint: `${this.analyticsSpeedingBasePath}/${aggregationRouteField}/summary`,
      method: HttpMethod.POST,
      body: JSON.stringify(snakecaseKeys(requestBody)),
    };

    const response = await this.requester.request(requestData);
    return camelcaseKeys(response.data.data);
  };

  public getSpeeding = async (body: SpeedingRequest, aggregatedBy: PageType): Promise<VehicleSpeedingTableRow[] | DriverSpeedingTableRow[]> => {
    const requestBody = this.getSpeedingRequestBody(body, aggregatedBy);
    const requestData = {
      endpoint: this.getSpeedingRequestEndpoint(aggregatedBy),
      method: HttpMethod.POST,
      body: JSON.stringify(snakecaseKeys(requestBody)),
    };

    const response = await this.requester.request(requestData);
    return camelcaseKeys(response.data.data, { deep: true });
  };

  public getTimeseriesChart = async (body: SpeedingChartRequest): Promise<TimeseriesChart[]> => {
    const requestBody = this.getSpeedingChartRequestBody(body);
    const requestData = {
      endpoint: `${this.analyticsSpeedingBasePath}/charts/timeseries`,
      method: HttpMethod.POST,
      body: JSON.stringify(snakecaseKeys(requestBody)),
    };

    const response = await this.requester.request(requestData);
    return camelcaseKeys(response.data.data, { deep: true });
  };

  public getHeatmapChart = async (body: SpeedingChartRequest): Promise<HeatmapChart[]> => {
    const requestBody = this.getSpeedingChartRequestBody(body);
    const requestData = {
      endpoint: `${this.analyticsSpeedingBasePath}/charts/heatmap`,
      method: HttpMethod.POST,
      body: JSON.stringify(snakecaseKeys(requestBody)),
    };

    const response = await this.requester.request(requestData);
    return camelcaseKeys(response.data.data, { deep: true });
  };

  public requestSpeedingReport = async (request: SpeedingReportRequest, aggregatedBy: ReportAggregatedBy): Promise<string> => {
    const requestBody = this.getSpeedingReportRequestBody(request);
    const reportRequestEndpoint = this.getReportRequestEndpoint(aggregatedBy);
    const requestData = {
      endpoint: reportRequestEndpoint,
      method: HttpMethod.POST,
      body: JSON.stringify(snakecaseKeys(requestBody)),
    };

    const response = await this.requester.request(requestData);
    return response.data;
  };

  private getReportRequestEndpoint = (reportAggregatedBy: ReportAggregatedBy): string => {
    switch (reportAggregatedBy) {
      case ReportAggregatedBy.VEHICLE:
        return `${this.analyticsSpeedingBasePath}/vehicle/report`;
      case ReportAggregatedBy.DRIVER:
        return `${this.analyticsSpeedingBasePath}/driver/report`;
      case ReportAggregatedBy.VEHICLE_GROUP:
        return `${this.analyticsSpeedingBasePath}/vehicle-group/report`;
      default:
        throw new Error('Invalid aggregated by');
    }
  };

  private getSpeedingRequestEndpoint = (aggregatedBy: PageType): string => {
    if (aggregatedBy === PageType.DRIVER) {
      return `${this.analyticsSpeedingBasePath}/driver`;
    }

    return `${this.analyticsSpeedingBasePath}/vehicle`;
  };

  private getSeveritiesBodyRequest = (severities?: Severity[]) => severities?.map(toCapitalSeverity);

  private getSpeedingRequestBody = (body: SpeedingRequest, aggregatedBy: PageType) => ({
    start_date: moment(body.startDate).toISOString(),
    end_date: moment(body.endDate).toISOString(),
    timezone: TimeZone.getTimezone(),
    eventType: body.eventType,
    severities: this.getSeveritiesBodyRequest(body.wantedSeverities),
    dashcam_filter: body.dashCamFilter,
    vehicle_ids: aggregatedBy === PageType.VEHICLE ? body.wantedIds : undefined,
    driver_ids: aggregatedBy === PageType.DRIVER ? body.wantedIds : undefined,
    vehicle_group_ids: body.wantedGroupIds,
    use_median_based_metrics: body.useMedianBasedMetrics,
  });

  private getSpeedingChartRequestBody = (body: SpeedingChartRequest) => ({
    start_date: moment(body.startDate).toISOString(),
    end_date: moment(body.endDate).toISOString(),
    timezone: TimeZone.getTimezone(),
    subdomain: body.subdomain,
    type: body.type,
    aggregation_field: body.aggregationField,
    wanted_ids: body.wantedIds,
    severities: this.getSeveritiesBodyRequest(body.wantedSeverities),
    use_median_based_metrics: body.useMedianBasedMetrics,
    dashcam_filter: body.dashCamFilter,
  });

  private getSpeedingReportRequestBody = (body: SpeedingReportRequest) => ({
    start_date: moment(body.startDate).toISOString(),
    end_date: moment(body.endDate).toISOString(),
    timezone: TimeZone.getTimezone(),
    wanted_ids: body.wantedIds,
    wanted_group_ids: body.wantedGroupIds,
    severities: this.getSeveritiesBodyRequest(body.wantedSeverities),
    event_type: body.eventType,
    report_format: body.reportFormat,
    destination_emails: body.destinationEmails,
    use_median_based_metrics: body.useMedianBasedMetrics,
    dashcam_filter: body.dashCamFilter,
  });
}

export default AnalyticsSpeedingRequester;
